import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import { 
  OrchestrationListResponse, 
  CourseOrchestration,
  CourseGenerationRequest,
  OrchestrationCreateResponse 
} from '@/types/orchestration.types';

// Retry-related types
export interface RetryRequest {
  stage: 'METADATA' | 'COURSE_OUTLINE' | 'COURSE_SKELETON' | 'MODULE_CONTENT'
    | 'IMAGE_GENERATION' | 'SCENE_GENERATION' | 'AUDIO_GENERATION' | 'VIDEO_GENERATION'
    | 'ALL_FAILED'
    | 'REGENERATE_MODULE' | 'REGENERATE_CONTENT_ITEM' | 'REGENERATE_SCENE' | 'REGENERATE_ASSESSMENT';
  force?: boolean;
  clear_existing?: boolean;
  scene_options?: {
    scene_ids?: string[];
    module_id?: string;
    content_item_id?: string;
    failed_only?: boolean;
  };
  max_retries?: number;
  // Granular regeneration options
  module_id?: string;
  content_item_id?: string;
  scene_id?: string;
  assessment_level?: 'module' | 'content_item';
  assessment_type?: 'pre' | 'post' | 'both';
}

export interface RetryResponse {
  success: boolean;
  description: string;
  data: {
    orchestration_id: string;
    stage: string;
    success: boolean;
    message: string;
    details?: {
      scenes_queued?: number;
      modules_affected?: number;
      estimated_time?: string;
    };
    retry_count: number;
    initiated_at: string;
  };
}

// Enhanced recovery types based on API documentation
export interface RecoveryResponse {
  success: boolean;
  data: {
    orchestration_id: string;
    recovery_status: string;
    recovery_actions_taken: string[];
    before_recovery: {
      orchestration_status: string;
      course_generation_status?: string;
      last_activity: string;
    };
    after_recovery: {
      orchestration_status: string;
      course_generation_status?: string;
      last_activity: string;
    };
    estimated_completion?: string;
    recovery_type: string;
  };
  message: string;
}

export interface SceneRetryRequest {
  scene_ids: string[];
  retry_type: 'AUDIO_ONLY' | 'VIDEO_ONLY' | 'BOTH' | 'CONTENT_REGENERATION';
  force?: boolean;
  clear_cache?: boolean;
  batch_size?: number;
  priority?: 'low' | 'normal' | 'high';
}

export interface SceneRetryResponse {
  success: boolean;
  data: {
    orchestration_id: string;
    retry_type: string;
    scenes_processed: {
      total_requested: number;
      queued_successfully: number;
      already_processing: number;
      not_found: number;
    };
    scene_details: Array<{
      scene_id: string;
      status: string;
      queue_position: number;
      estimated_completion: string;
    }>;
    batch_info: {
      batch_id: string;
      priority: string;
      estimated_total_time: string;
    };
  };
  message: string;
}

export interface DetailedStageStatus {
  orchestration_id: string;
  metadata_status?: {
    status: string;
    retry_count: number;
    last_error?: string;
    completed_at?: string;
    can_retry: boolean;
  };
  outline_status?: {
    status: string;
    modules_generated?: number;
    retry_count: number;
    can_retry: boolean;
  };
  content_status?: {
    status: string;
    modules_completed?: number;
    total_modules?: number;
    retry_count: number;
    last_error?: string;
    can_retry: boolean;
    failed_modules?: string[];
  };
  scene_status?: {
    total_scenes: number;
    completed: number;
    failed: number;
    pending: number;
    retry_count: number;
    can_retry: boolean;
    failed_scenes?: string[];
  };
  image_status?: {
    status: string;
    images_generated?: number;
    retry_count: number;
    can_retry: boolean;
  };
  audio_status?: {
    total_scenes: number;
    audio_generated?: number;
    failed?: number;
    retry_count: number;
    can_retry: boolean;
    failed_scenes?: string[];
  };
  video_status?: {
    total_scenes: number;
    videos_generated?: number;
    failed?: number;
    retry_count: number;
    can_retry: boolean;
  };
  overall_status: string;
  can_retry: {
    metadata: boolean;
    outline: boolean;
    content: boolean;
    scenes: boolean;
    image: boolean;
    audio: boolean;
    video: boolean;
  };
  retry_recommendations?: Array<{
    stage: string;
    reason: string;
    suggested_action: string;
  }>;
}

export interface StageStatusResponse {
  success: boolean;
  description: string;
  data: DetailedStageStatus;
}

export interface StageStatusResponseOld {
  success: boolean;
  description: string;
  data: {
    orchestration_id: string;
    metadata_status?: {
      status: string;
      retry_count: number;
      last_error?: string;
      completed_at?: string;
    };
    outline_status?: {
      status: string;
      modules_generated?: number;
      retry_count: number;
    };
    content_status?: {
      status: string;
      modules_completed?: number;
      total_modules?: number;
      retry_count: number;
    };
    scene_status?: {
      total_scenes: number;
      completed: number;
      failed: number;
      pending: number;
      retry_count: number;
    };
    image_status?: {
      status: string;
      images_generated?: number;
      retry_count: number;
    };
    audio_status?: {
      total_scenes: number;
      audio_generated?: number;
      failed?: number;
      retry_count: number;
    };
    video_status?: {
      total_scenes: number;
      videos_generated?: number;
      failed?: number;
      retry_count: number;
    };
    overall_status: string;
    can_retry: {
      metadata: boolean;
      outline: boolean;
      content: boolean;
      scenes: boolean;
      image: boolean;
      audio: boolean;
      video: boolean;
    };
  };
}

class CourseGenerationService {
  private pollingInterval: NodeJS.Timeout | null = null;

  async getCourseOrchestrationList(filters: any = {}): Promise<OrchestrationListResponse> {
    try {
      const response = await apiClient.post<OrchestrationListResponse>(API_ENDPOINTS.COURSE_ORCHESTRATION.LIST, filters);
      return response.data;
    } catch (error) {
      console.error('Error fetching orchestration list:', error);
      throw error;
    }
  }

  async initiateCourseGeneration(data: CourseGenerationRequest): Promise<CourseOrchestration> {
    try {
      const response = await apiClient.post<OrchestrationCreateResponse>(API_ENDPOINTS.COURSE_ORCHESTRATION.CREATE_INITIATE, data);
      return response.data.data;
    } catch (error: any) {
      console.error('Error initiating course generation:', error);
      // Provide more detailed error message
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  async getOrchestrationById(orchestrationId: string): Promise<CourseOrchestration> {
    try {
      const response = await apiClient.get<OrchestrationCreateResponse>(API_ENDPOINTS.COURSE_ORCHESTRATION.GET_BY_ID(orchestrationId));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching orchestration:', error);
      throw error;
    }
  }

  async getOrchestrationStatus(orchestrationId: string): Promise<CourseOrchestration> {
    try {
      const response = await apiClient.get<OrchestrationCreateResponse>(API_ENDPOINTS.COURSE_ORCHESTRATION.GET_STATUS(orchestrationId));
      const data = response.data.data;
      
      // Map root level costs to live_api_usage if needed
      if (data && (data.total_tts_cost !== undefined || data.total_search_cost !== undefined)) {
        if (!data.live_api_usage) {
          data.live_api_usage = {
            total_tts_characters: 0,
            total_tts_cost: 0,
            total_stt_seconds: 0,
            total_stt_cost: 0,
            total_search_queries: 0,
            total_search_cost: 0,
            total_image_generations: 0,
            total_image_cost: 0,
            total_api_calls: 0,
            last_updated: new Date().toISOString()
          };
        }
        
        // Map costs from root level to live_api_usage
        data.live_api_usage.total_tts_cost = data.live_api_usage.total_tts_cost || data.total_tts_cost || 0;
        data.live_api_usage.total_stt_cost = data.live_api_usage.total_stt_cost || data.total_stt_cost || 0;
        data.live_api_usage.total_search_cost = data.live_api_usage.total_search_cost || data.total_search_cost || 0;
        data.live_api_usage.total_search_queries = data.live_api_usage.total_search_queries || data.total_search_queries || 0;
        
        // Estimate TTS characters if not provided (rough estimate based on typical TTS pricing)
        if (!data.live_api_usage.total_tts_characters && data.total_tts_cost && data.total_tts_cost > 0) {
          // Assuming ~$0.015 per 1000 characters for TTS
          data.live_api_usage.total_tts_characters = Math.round((data.total_tts_cost / 0.015) * 1000);
        }
      }
      
      // Map overall costs if not present
      if (!data.overall_cost && data.total_overall_cost !== undefined) {
        data.overall_cost = {
          llm_cost: data.total_llm_cost || 0,
          api_cost: (data.total_tts_cost || 0) + 
                    (data.total_stt_cost || 0) + 
                    (data.total_search_cost || 0),
          total_cost: data.total_overall_cost || 0,
          last_updated: data.updatedAt || new Date().toISOString()
        };
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching orchestration status:', error);
      throw error;
    }
  }

  // Alias for backward compatibility
  async getCourseGenerationStatus(orchestrationId: string): Promise<CourseOrchestration> {
    return this.getOrchestrationStatus(orchestrationId);
  }

  startPollingStatus(orchestrationId: string, callback: (status: CourseOrchestration) => void, interval = 5000) {
    this.stopPollingStatus(); // Clear any existing polling

    // Initial fetch
    this.getOrchestrationStatus(orchestrationId)
      .then(callback)
      .catch(error => console.error('Polling error:', error));

    // Set up interval
    this.pollingInterval = setInterval(async () => {
      try {
        const status = await this.getOrchestrationStatus(orchestrationId);
        callback(status);
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, interval);
  }

  stopPollingStatus() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  getProgressPercentage(orchestration: CourseOrchestration): number {
    if (!orchestration) return 0;

    const stages = [
      orchestration.metadata_generation_status,
      orchestration.image_generation_status,
      orchestration.course_skeleton_generation_status,
      orchestration.module_content_expansion_status
    ];

    const completedStages = stages.filter(stage =>
      stage?.status?.toLowerCase() === 'completed'
    ).length;

    return (completedStages / stages.length) * 100;
  }

  // ===========================================
  // RETRY OPERATIONS
  // ===========================================

  /**
   * Manually retry a specific stage of course orchestration
   * @param orchestrationId - The orchestration ID
   * @param retryRequest - Retry configuration
   * @returns Retry operation result
   */
  async retryOrchestrationStage(orchestrationId: string, retryRequest: RetryRequest): Promise<RetryResponse> {
    try {
      const response = await apiClient.post<RetryResponse>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.RETRY(orchestrationId),
        retryRequest
      );
      return response.data;
    } catch (error) {
      console.error('Error retrying orchestration stage:', error);
      throw error;
    }
  }

  /**
   * Retry specific scenes within an orchestration
   * @param orchestrationId - The orchestration ID
   * @param sceneRetryRequest - Scene-specific retry configuration
   * @returns Scene retry result
   */
  async retryScenes(orchestrationId: string, sceneRetryRequest: {
    scene_ids?: string[];
    module_id?: string;
    content_item_id?: string;
    failed_only?: boolean;
    force?: boolean;
  }): Promise<RetryResponse> {
    try {
      const response = await apiClient.post<RetryResponse>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.RETRY_SCENES(orchestrationId),
        sceneRetryRequest
      );
      return response.data;
    } catch (error) {
      console.error('Error retrying scenes:', error);
      throw error;
    }
  }

  /**
   * Get detailed stage status for retry planning
   * @param orchestrationId - The orchestration ID
   * @returns Detailed stage status
   */
  async getStageStatus(orchestrationId: string): Promise<StageStatusResponse> {
    try {
      const response = await apiClient.get<StageStatusResponse>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.STAGE_STATUS(orchestrationId)
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching stage status:', error);
      throw error;
    }
  }

  /**
   * Recover a single stuck orchestration automatically
   * @param orchestrationId - The orchestration ID
   * @returns Recovery operation result
   */
  async recoverSingleOrchestration(orchestrationId: string): Promise<RecoveryResponse> {
    try {
      const response = await apiClient.post<RecoveryResponse>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.RECOVER_SINGLE(orchestrationId)
      );
      return response.data;
    } catch (error) {
      console.error('Error recovering orchestration:', error);
      throw error;
    }
  }

  /**
   * Enhanced scene retry with full control options
   * @param orchestrationId - The orchestration ID
   * @param sceneRetryRequest - Enhanced scene retry configuration
   * @returns Scene retry result
   */
  async retryScenesBatch(orchestrationId: string, sceneRetryRequest: SceneRetryRequest): Promise<SceneRetryResponse> {
    try {
      const response = await apiClient.post<SceneRetryResponse>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.RETRY_SCENES_BATCH(orchestrationId),
        sceneRetryRequest
      );
      return response.data;
    } catch (error) {
      console.error('Error retrying scenes batch:', error);
      throw error;
    }
  }

  // ===========================================
  // GRANULAR REGENERATION
  // ===========================================

  async regenerateModule(orchestrationId: string, moduleId: string): Promise<RetryResponse> {
    return this.retryOrchestrationStage(orchestrationId, {
      stage: 'REGENERATE_MODULE',
      force: true,
      module_id: moduleId,
    });
  }

  async regenerateContentItem(orchestrationId: string, contentItemId: string): Promise<RetryResponse> {
    return this.retryOrchestrationStage(orchestrationId, {
      stage: 'REGENERATE_CONTENT_ITEM',
      force: true,
      content_item_id: contentItemId,
    });
  }

  async regenerateScene(orchestrationId: string, sceneId: string): Promise<RetryResponse> {
    return this.retryOrchestrationStage(orchestrationId, {
      stage: 'REGENERATE_SCENE',
      force: true,
      scene_id: sceneId,
    });
  }

  async regenerateAssessment(
    orchestrationId: string,
    level: 'module' | 'content_item',
    levelId: string,
    assessmentType: 'pre' | 'post' | 'both'
  ): Promise<RetryResponse> {
    return this.retryOrchestrationStage(orchestrationId, {
      stage: 'REGENERATE_ASSESSMENT',
      force: true,
      assessment_level: level,
      assessment_type: assessmentType,
      module_id: level === 'module' ? levelId : undefined,
      content_item_id: level === 'content_item' ? levelId : undefined,
    });
  }

  /**
   * Check if an orchestration is stuck (no progress for a threshold period)
   * Threshold depends on content_delivery_type:
   *   - video_presentation: 8 hours (video rendering takes much longer)
   *   - html / html_with_audio: 1 hour
   * @param orchestration - The orchestration object
   * @returns Whether the orchestration is stuck
   */
  isOrchestrationStuck(orchestration: CourseOrchestration): boolean {
    if (!orchestration.updatedAt) return false;
    if (orchestration.orchestration_status !== 'PROCESSING') return false;

    const is_video = orchestration.content_delivery_type === 'video_presentation' || orchestration.content_delivery_type === 'avatar_video';
    const stuck_threshold_minutes = is_video ? 8 * 60 : 60;

    const lastUpdate = new Date(orchestration.updatedAt);
    const minutesSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60);

    return minutesSinceUpdate > stuck_threshold_minutes;
  }

  /**
   * Get human-readable recovery action descriptions
   * @param recoveryType - Recovery type from API
   * @returns Human-readable description
   */
  getRecoveryTypeDescription(recoveryType: string): string {
    const descriptions: Record<string, string> = {
      'stage_failure_recovery': 'Recovered from failed stage',
      'timeout_recovery': 'Recovered from timeout condition',
      'scene_level_recovery': 'Recovered individual scenes',
      'queue_recovery': 'Requeued stuck messages'
    };
    return descriptions[recoveryType] || recoveryType;
  }

  /**
   * Update course orchestration metadata
   * @param orchestrationId - The orchestration ID
   * @param updateData - Updated orchestration data
   * @returns Updated orchestration
   */
  async updateOrchestration(orchestrationId: string, updateData: Partial<CourseGenerationRequest>): Promise<CourseOrchestration> {
    try {
      const response = await apiClient.put<OrchestrationCreateResponse>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.UPDATE(orchestrationId),
        updateData
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating orchestration:', error);
      throw error;
    }
  }

  /**
   * Delete (soft delete) a course orchestration
   * @param orchestrationId - The orchestration ID
   * @returns Deletion result
   */
  async deleteOrchestration(orchestrationId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; description: string; data: any }>(
        API_ENDPOINTS.COURSE_ORCHESTRATION.DELETE(orchestrationId)
      );
      return {
        success: response.data.success,
        message: response.data.description
      };
    } catch (error) {
      console.error('Error deleting orchestration:', error);
      throw error;
    }
  }

  /**
   * Check if a stage can be retried based on its current status
   * @param status - Current stage status
   * @returns Whether the stage can be retried
   */
  canRetryStage(status: string | undefined): boolean {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'failed' || lowerStatus === 'not_started' || lowerStatus === 'pending';
  }

  /**
   * Get human-readable stage names
   * @param stage - Stage enum value
   * @returns Human-readable stage name
   */
  getStageDisplayName(stage: string): string {
    const stageNames: Record<string, string> = {
      'METADATA': 'Metadata Generation',
      'COURSE_OUTLINE': 'Course Outline',
      'MODULE_CONTENT': 'Module Content',
      'IMAGE_GENERATION': 'Image Generation',
      'SCENE_GENERATION': 'Scene Generation',
      'AUDIO_GENERATION': 'Audio Generation',
      'VIDEO_GENERATION': 'Video Generation',
      'ALL_FAILED': 'All Failed Stages'
    };
    return stageNames[stage] || stage;
  }

  /**
   * Approve course skeleton to proceed to module content expansion
   * @param orchestrationId - Course orchestration ID
   * @param comments - Optional approval comments
   */
  async approveSkeleton(orchestrationId: string, comments?: string): Promise<any> {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.COURSE_ORCHESTRATION.BASE}/${orchestrationId}/skeleton/approve`, {
        comments
      });
      return response.data;
    } catch (error) {
      console.error('Error approving skeleton:', error);
      throw error;
    }
  }

  /**
   * Reject course skeleton with feedback
   * @param orchestrationId - Course orchestration ID
   * @param reason - Rejection reason
   * @param suggestions - Improvement suggestions
   */
  async rejectSkeleton(orchestrationId: string, reason?: string, suggestions?: string): Promise<any> {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.COURSE_ORCHESTRATION.BASE}/${orchestrationId}/skeleton/reject`, {
        reason,
        suggestions
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting skeleton:', error);
      throw error;
    }
  }

  /**
   * Regenerate course skeleton with user feedback
   * @param orchestrationId - Course orchestration ID
   * @param feedback - User feedback for improvement
   */
  async regenerateSkeleton(orchestrationId: string, feedback?: string): Promise<any> {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.COURSE_ORCHESTRATION.BASE}/${orchestrationId}/skeleton/regenerate`, {
        feedback
      });
      return response.data;
    } catch (error) {
      console.error('Error regenerating skeleton:', error);
      throw error;
    }
  }

  /**
   * Get course skeleton status
   * @param orchestrationId - Course orchestration ID
   */
  async getSkeletonStatus(orchestrationId: string): Promise<any> {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.COURSE_ORCHESTRATION.BASE}/${orchestrationId}/skeleton/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting skeleton status:', error);
      throw error;
    }
  }

  /**
   * Regenerate entire course from scratch
   * Resets all stages and starts fresh generation
   * @param orchestrationId - Course orchestration ID
   * @param reason - Reason for regeneration
   * @param force - Force regeneration even if not eligible
   */
  async regenerateFullCourse(
    orchestrationId: string,
    reason?: string,
    force?: boolean
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.COURSE_ORCHESTRATION.BASE}/${orchestrationId}/regenerate-full-course`,
        { reason, force }
      );
      return response.data;
    } catch (error) {
      console.error('Error regenerating full course:', error);
      throw error;
    }
  }
}

export const courseGenerationService = new CourseGenerationService();
export default courseGenerationService;
